import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/styles.css";
import "../../../styles/global.css";
import AccreditationButton from "../../AccreditationButton/AccreditationButton";
import AccreditationInput from "../../AccreditationInput/AccreditationInput";
import Loader from "../../Loader/Loader";
import { icons } from "../../../constants";
import { TranslationsContext } from "../../../utils/TranslationsContext";
import { useAlert } from "../../../utils/AlertContext";
import { apiRoutes } from "../../../utils/apiRoutes";
import { fetchData, createData } from "../../../utils/methods";
import { formatDateBg } from "../../../utils/belgradeTime";

// Copies persons a korisnik registered for one of their other events (past,
// ongoing or upcoming) into this one (gs.be resolvers/uvozOsoba.js): new ids, status back to
// "prijavljen", same data and photos. korisnikId is the persons tab's
// korisnik filter for admins/moderators; null for a user importing their own.
const ImportPersons = ({ eventId, korisnikId, korisnikName, onClose, onImported }) => {
  const { t } = useContext(TranslationsContext);
  const { showAlert } = useAlert();
  const navigate = useNavigate();

  const [events, setEvents] = useState(null);
  const [sourceId, setSourceId] = useState(null);
  const [persons, setPersons] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [isImporting, setIsImporting] = useState(false);

  const withKorisnik = (params) =>
    korisnikId ? `${params}&korisnikId=${encodeURIComponent(korisnikId)}` : params;

  useEffect(() => {
    let cancelled = false;
    fetchData(
      withKorisnik(`${apiRoutes.getImportEvents}?dogadjajId=${encodeURIComponent(eventId)}`),
      showAlert,
      navigate,
      t,
    ).then((result) => {
      if (cancelled) return;
      const list = Array.isArray(result?.data) ? result.data : [];
      setEvents(list);
      // Preselect the first one (gs.be lists them newest first).
      if (list.length) setSourceId(list[0].id);
    });
    return () => {
      cancelled = true;
    };
  }, [eventId, korisnikId]);

  useEffect(() => {
    if (!sourceId) return undefined;
    let cancelled = false;
    setPersons(null);
    setSelected(new Set());
    fetchData(
      withKorisnik(
        `${apiRoutes.getImportPersons}?dogadjajId=${encodeURIComponent(eventId)}&izvorDogadjajId=${encodeURIComponent(sourceId)}`,
      ),
      showAlert,
      navigate,
      t,
    ).then((result) => {
      if (cancelled) return;
      setPersons(Array.isArray(result?.data) ? result.data : []);
    });
    return () => {
      cancelled = true;
    };
  }, [sourceId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const selectable = useMemo(
    () => (persons || []).filter((p) => !p.vecDodat),
    [persons],
  );
  const allSelected = selectable.length > 0 && selected.size === selectable.length;

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(selectable.map((p) => p.id)));
  };

  const handleImport = async () => {
    if (!selected.size || isImporting) return;
    setIsImporting(true);
    try {
      const result = await createData(
        apiRoutes.importPersons,
        {
          dogadjajId: eventId,
          izvorDogadjajId: sourceId,
          osobaIds: [...selected],
          korisnikId: korisnikId || null,
        },
        showAlert,
        navigate,
        t,
      );
      if (!result) return;
      const imported = Array.isArray(result.data?.uvezeno) ? result.data.uvezeno : [];
      const skipped = result.data?.preskoceno || 0;
      onImported?.(imported);
      showAlert(
        `${t("IMPORT_PERSONS_DONE")}: ${imported.length}` +
          (skipped ? ` · ${t("IMPORT_PERSONS_SKIPPED")}: ${skipped}` : ""),
        "success",
      );
      onClose();
    } finally {
      setIsImporting(false);
    }
  };

  const eventLabel = (ev) =>
    `${ev.naziv} · ${formatDateBg(ev.trajanje_od)} (${ev.brojOsoba})`;

  return (
    <div
      className="flex-direction-column items-center justify-center confirmation-modal-wrapper"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex-direction-column gap-16 import-persons-modal">
        <div className="flex-direction-row items-center justify-between gap-12">
          <div className="flex-direction-column gap-4">
            <p className="inria-sans-700 font-20 import-persons-title">
              {t("IMPORT_PERSONS_TITLE")}
            </p>
            {korisnikName && (
              <p className="inria-sans-400 font-14 import-persons-muted">
                {t("IMPORT_PERSONS_FOR")} <strong>{korisnikName}</strong>
              </p>
            )}
          </div>
          <img
            className="pointer"
            src={icons.close_gray}
            onClick={onClose}
            alt="X"
            width={24}
            height={24}
          />
        </div>

        <p className="inria-sans-400 font-14 import-persons-muted">
          {t("IMPORT_PERSONS_NOTE")}
        </p>

        {events === null ? (
          <Loader />
        ) : events.length === 0 ? (
          <p className="inria-sans-400 font-14 import-persons-empty">
            {t("IMPORT_PERSONS_NO_EVENTS")}
          </p>
        ) : (
          <>
            <AccreditationInput
              type="dropdown"
              className="modal-input"
              label={t("IMPORT_PERSONS_FROM_EVENT")}
              selectedOption={eventLabel(events.find((ev) => ev.id === sourceId) || events[0])}
              setSelectedOption={(key) => setSourceId(key)}
              options={events.map((ev) => ({ key: ev.id, value: eventLabel(ev) }))}
            />

            {persons === null ? (
              <Loader />
            ) : persons.length === 0 ? (
              <p className="inria-sans-400 font-14 import-persons-empty">
                {t("IMPORT_PERSONS_NO_PERSONS")}
              </p>
            ) : (
              <div className="flex-direction-column import-persons-list">
                <label className="flex-direction-row items-center gap-12 pointer import-persons-row import-persons-row-head">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    disabled={!selectable.length}
                    onChange={toggleAll}
                  />
                  <span className="inria-sans-700 font-14">
                    {t("SELECT_ALL")} ({selected.size}/{selectable.length})
                  </span>
                </label>
                {persons.map((p) => (
                  <label
                    key={p.id}
                    className={`flex-direction-row items-center gap-12 import-persons-row ${
                      p.vecDodat ? "disabled" : "pointer"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(p.id)}
                      disabled={p.vecDodat}
                      onChange={() => toggle(p.id)}
                    />
                    {p.slika_avatar ? (
                      <img className="import-persons-avatar" src={p.slika_avatar} alt="" />
                    ) : (
                      <span className="import-persons-avatar" />
                    )}
                    <span className="flex-direction-column import-persons-name">
                      <span className="inria-sans-700 font-14">
                        {p.ime} {p.prezime}
                      </span>
                      <span className="inria-sans-400 font-12 import-persons-muted">
                        {[p.entitet, p.pozicija].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                    <span className="inria-sans-400 font-12 import-persons-muted import-persons-meta">
                      {p.vecDodat ? t("IMPORT_PERSONS_ALREADY_ADDED") : p.kategorija || ""}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </>
        )}

        <div className="flex-direction-row gap-12 justify-end">
          <AccreditationButton
            type="close"
            title={t("BTN_CLOSE") || "Zatvori"}
            onClick={onClose}
            className="modal-shell-action"
            icon={icons.close_neutral}
          />
          <AccreditationButton
            type="save"
            title={`${t("IMPORT")} (${selected.size})`}
            onClick={handleImport}
            className="modal-shell-action"
            icon={icons.save_blue}
            disabled={!selected.size || isImporting}
          />
        </div>
      </div>
    </div>
  );
};

export default ImportPersons;
